import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Chip,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import DriveEtaOutlinedIcon from "@mui/icons-material/DriveEtaOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { api } from "common";
import {
  ID_DOC_REJECT_REASONS,
  idDocRejectReasonLabel,
} from "common/src/other/idDocumentRejection";
import { C2, C2_FONT, isReuploadedAfterReject } from "./Table/C2UserTable";

/**
 * Modal admin: revisar documentos (Cédula / Licencia de conducción), aprobar o rechazar con motivo.
 */
export default function VerifyIdDocumentModal({ open, user, onClose, onDone }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { editUser, fetchUsersOnce } = api;
  const [step, setStep] = useState("review"); // review | reject
  const [rejectCode, setRejectCode] = useState("reupload_document");
  const [rejectOther, setRejectOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Cédula, 1: Licencia

  useEffect(() => {
    if (open) {
      setStep("review");
      setRejectCode("reupload_document");
      setRejectOther("");
      setBusy(false);
      setActiveTab(0);
    }
  }, [open, user?.id]);

  if (!user) return null;

  const isEn = String(i18n.language || "es").toLowerCase().startsWith("en");
  const isDriver = user.usertype === "driver";
  const status = user.verifyIdStatus || (user.verifyIdApproved ? "approved" : "pending");

  const hasAnyDoc = Boolean(
    user.verifyIdImage || user.licenseImage || user.licenseImageBack
  );

  const persist = async (patch) => {
    setBusy(true);
    try {
      await dispatch(
        editUser(user.id, {
          ...user,
          ...patch,
        })
      );
      if (typeof fetchUsersOnce === "function") {
        dispatch(fetchUsersOnce({ force: true }));
      }
      if (onDone) onDone(patch);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = () => {
    persist({
      // Aprobar documentos aprueba también la cuenta (mismo efecto que el switch "Cuenta aprobada").
      approved: true,
      adminApprovedTrue: true,
      verifyIdApproved: true,
      verifyIdStatus: "approved",
      verifyIdRejectCode: null,
      verifyIdRejectReason: null,
      verifyIdRejectOther: null,
      verifyIdReviewedAt: Date.now(),
      docsReuploadedAfterReject: false,
    });
  };

  const handleConfirmReject = () => {
    if (rejectCode === "other" && !String(rejectOther || "").trim()) return;
    const reason = idDocRejectReasonLabel(rejectCode, i18n.language, rejectOther);
    persist({
      verifyIdApproved: false,
      verifyIdStatus: "rejected",
      verifyIdRejectCode: rejectCode,
      verifyIdRejectReason: reason,
      verifyIdRejectOther: rejectCode === "other" ? String(rejectOther).trim() : null,
      verifyIdReviewedAt: Date.now(),
      docsReuploadedAfterReject: false,
    });
  };

  const openImageFullscreen = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal open={!!open} onClose={busy ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94%", sm: isDriver ? 680 : 560 },
          maxHeight: "92vh",
          overflowY: "auto",
          bgcolor: C2.white,
          borderRadius: "20px",
          border: `1px solid ${C2.border}`,
          boxShadow: "0 16px 48px rgba(26,15,59,0.25)",
          p: { xs: 2, sm: 3 },
          outline: "none",
        }}
      >
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontWeight: 800,
            fontSize: "1.25rem",
            color: C2.sidebar,
            mb: 0.5,
          }}
        >
          {t("documents") || "Documentos"} · {isDriver ? "Conductor" : "Usuario"}
        </Typography>
        <Typography sx={{ fontFamily: C2_FONT, fontSize: 13, color: C2.muted, mb: 1.5 }}>
          {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Usuario"}
          {user.email ? ` · ${user.email}` : ""}
          {user.mobile ? ` · ${user.mobile}` : ""}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={
              status === "approved"
                ? t("approved") || "Aprobado"
                : status === "rejected"
                ? t("not_approved") || "Rechazado"
                : "Pendiente"
            }
            sx={{
              fontFamily: C2_FONT,
              fontWeight: 700,
              bgcolor:
                status === "approved"
                  ? "rgba(18,183,106,0.15)"
                  : status === "rejected"
                  ? "rgba(223,46,56,0.12)"
                  : "rgba(255,222,89,0.35)",
              color:
                status === "approved"
                  ? "#0E7A45"
                  : status === "rejected"
                  ? "#DF2E38"
                  : "#B45309",
            }}
          />

          {user.verifyIdRejectReason && status === "rejected" && (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.78rem", color: "#DF2E38", fontWeight: 600 }}>
              Motivo: {user.verifyIdRejectReason}
            </Typography>
          )}
        </Box>

        {isReuploadedAfterReject(user) && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: "12px",
              fontFamily: C2_FONT,
              fontWeight: 600,
              fontSize: "0.85rem",
              border: "1px solid #fed7aa",
              bgcolor: "#fffbeb",
              color: "#9a3412",
              "& .MuiAlert-icon": { color: "#ea580c" },
            }}
          >
            🔔 <strong>Nuevo documento cargado:</strong> Fue rechazado previamente y volvió a subir soportes para nueva revisión.
          </Alert>
        )}

        {/* Pestañas para conductores: Cédula / Licencia de conducción */}
        {isDriver && (
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{
              mb: 2,
              borderBottom: `1.5px solid ${C2.border}`,
              "& .MuiTab-root": {
                fontFamily: C2_FONT,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.88rem",
                color: C2.muted,
                minHeight: 42,
                gap: 0.8,
                "&.Mui-selected": {
                  color: C2.violet,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: C2.violet,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <span>
                  1. Cédula / ID {user.verifyIdImage ? "✓" : "(falta foto)"}
                </span>
              }
            />
            <Tab
              icon={<DriveEtaOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <span>
                  2. Licencia de conducción {user.licenseImage ? "✓" : "(falta)"}
                </span>
              }
            />
          </Tabs>
        )}

        {/* CONTENIDO PESTAÑA 0: CÉDULA / ID */}
        {(!isDriver || activeTab === 0) && (
          <Box>
            <Box
              sx={{
                bgcolor: C2.bg,
                border: `1px solid ${C2.border}`,
                borderRadius: "14px",
                px: 2,
                py: 1.5,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: C2_FONT,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: C2.muted,
                }}
              >
                Número Documento ID/Cédula
              </Typography>
              <Typography
                sx={{
                  fontFamily: C2_FONT,
                  fontWeight: 800,
                  fontSize: 18,
                  color: C2.sidebar,
                  mt: 0.5,
                  wordBreak: "break-word",
                }}
              >
                {user.verifyId || t("no_data_available") || "—"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 220,
                bgcolor: C2.lavender,
                borderRadius: "16px",
                border: `1px solid ${C2.border}`,
                mb: 2,
                overflow: "hidden",
                position: "relative",
                p: 1,
              }}
            >
              {user.verifyIdImage ? (
                <>
                  <Tooltip title="Abrir imagen en tamaño completo">
                    <IconButton
                      onClick={() => openImageFullscreen(user.verifyIdImage)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        color: C2.violet,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        "&:hover": { bgcolor: "#FFFFFF" },
                      }}
                      size="small"
                    >
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Box
                    component="img"
                    src={user.verifyIdImage}
                    alt="Documento ID / Cédula"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 340,
                      objectFit: "contain",
                      display: "block",
                      borderRadius: "10px",
                    }}
                  />
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: C2_FONT, color: C2.muted, fontSize: "0.92rem", fontWeight: 600 }}>
                    No hay documento cargado. Sube al menos un documento para guardar.
                  </Typography>
                  <Typography sx={{ fontFamily: C2_FONT, color: "#DF2E38", fontSize: "0.8rem", mt: 0.8, fontWeight: 700 }}>
                    ⚠️ Falta la imagen de la cédula/ID. Puedes rechazar para solicitarla al usuario.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* CONTENIDO PESTAÑA 1: LICENCIA DE CONDUCCIÓN (SOLO CONDUCTORES) */}
        {isDriver && activeTab === 1 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* Licencia - Frente */}
              <Box
                sx={{
                  bgcolor: C2.bg,
                  borderRadius: "14px",
                  border: `1px solid ${C2.border}`,
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "0.82rem", color: C2.sidebar }}>
                    Licencia · Frente
                  </Typography>
                  {user.licenseImage && (
                    <Tooltip title="Abrir en tamaño completo">
                      <IconButton
                        size="small"
                        onClick={() => openImageFullscreen(user.licenseImage)}
                        sx={{ color: C2.violet, p: 0.3 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 180,
                    bgcolor: C2.lavender,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: 0.5,
                  }}
                >
                  {user.licenseImage ? (
                    <Box
                      component="img"
                      src={user.licenseImage}
                      alt="Licencia frente"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 220,
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.8rem", color: C2.muted, textAlign: "center", p: 1 }}>
                      No se ha subido foto del frente
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Licencia - Reverso */}
              <Box
                sx={{
                  bgcolor: C2.bg,
                  borderRadius: "14px",
                  border: `1px solid ${C2.border}`,
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "0.82rem", color: C2.sidebar }}>
                    Licencia · Reverso
                  </Typography>
                  {user.licenseImageBack && (
                    <Tooltip title="Abrir en tamaño completo">
                      <IconButton
                        size="small"
                        onClick={() => openImageFullscreen(user.licenseImageBack)}
                        sx={{ color: C2.violet, p: 0.3 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 180,
                    bgcolor: C2.lavender,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: 0.5,
                  }}
                >
                  {user.licenseImageBack ? (
                    <Box
                      component="img"
                      src={user.licenseImageBack}
                      alt="Licencia reverso"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 220,
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.8rem", color: C2.muted, textAlign: "center", p: 1 }}>
                      No se ha subido foto del reverso
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* PASO 2: FORMULARIO DE RECHAZO CON MOTIVOS */}
        {step === "reject" ? (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar, mb: 1 }}>
              Motivo del rechazo
            </Typography>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: 13, color: C2.muted, mb: 1.5 }}>
              Este motivo se mostrará al usuario en la app para que vuelva a subir los documentos necesarios.
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={rejectCode} onChange={(e) => setRejectCode(e.target.value)}>
                {ID_DOC_REJECT_REASONS.map((r, idx) => (
                  <FormControlLabel
                    key={r.code}
                    value={r.code}
                    control={
                      <Radio sx={{ color: C2.violet, "&.Mui-checked": { color: C2.violet } }} />
                    }
                    label={
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.92rem", color: C2.sidebar }}>
                        {idx + 1}. {isEn ? r.en : r.es}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {rejectCode === "other" ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={rejectOther}
                onChange={(e) => setRejectOther(e.target.value)}
                placeholder="Describe el motivo…"
                sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: C2_FONT } }}
              />
            ) : null}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2.5, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => setStep("review")}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontFamily: C2_FONT,
                  fontWeight: 700,
                  borderColor: C2.border,
                  color: C2.sidebar,
                }}
              >
                {t("cancel") || "Cancelar"}
              </Button>
              <Button
                variant="contained"
                disabled={busy || (rejectCode === "other" && !String(rejectOther).trim())}
                onClick={handleConfirmReject}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontFamily: C2_FONT,
                  fontWeight: 700,
                  bgcolor: "#DF2E38",
                  "&:hover": { bgcolor: "#B82028" },
                }}
              >
                Confirmar rechazo
              </Button>
            </Box>
          </Box>
        ) : (
          /* PASO 1: BOTONES CERRAR / RECHAZAR / APROBAR */
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", mt: 1 }}>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={onClose}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontFamily: C2_FONT,
                fontWeight: 700,
                borderColor: C2.border,
                color: C2.sidebar,
                px: 2.5,
              }}
            >
              {t("close") || "Cerrar"}
            </Button>

            {/* BOTÓN RECHAZAR: SIEMPRE HABILITADO PARA EL ADMIN */}
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => setStep("reject")}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontFamily: C2_FONT,
                fontWeight: 700,
                borderColor: "#DF2E38",
                color: "#DF2E38",
                px: 2.5,
                "&:hover": {
                  borderColor: "#B82028",
                  bgcolor: "rgba(223,46,56,0.08)",
                },
              }}
            >
              Rechazar
            </Button>

            {/* BOTÓN APROBAR: HABILITADO SI TIENE AL MENOS UN DOCUMENTO */}
            <Button
              variant="contained"
              disabled={busy || !hasAnyDoc}
              onClick={handleApprove}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontFamily: C2_FONT,
                fontWeight: 700,
                px: 3,
                background: `linear-gradient(90deg, ${C2.violet}, ${C2.bright})`,
                boxShadow: "0 4px 14px rgba(100,17,237,0.35)",
                "&:disabled": {
                  background: "#E2E8F0",
                  color: "#94A3B8",
                },
              }}
            >
              Aprobar
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
